import { ChangeDetectionStrategy, Component, computed, effect, input, signal, untracked } from '@angular/core';
import { IDataboxLivesIcon } from 'src/app/api/modules/core/dynamic/databoxes/lives-icon/IDataboxLivesIcon';
import { GenerateFaIcon } from 'src/app/factories/generators/icon-fa.generator';
import { FaIconComponent } from '../../../../../icons/components/fa-icon/fa-icon.component';

/**
 * The icon used when the configuration does not provide one.
 */
function DEFAULT_ICON() {
  return GenerateFaIcon({
    name: 'fas:heart',
    state: 'default',
  });
}

/**
 * Displays the user's remaining lives inside a DataboxContainer: a configured
 * icon followed by the live count pushed in through {@link update}.
 *
 * An unconfigured databox renders the built-in defaults (a heart icon and
 * zero lives).
 *
 * See {@link IDataboxLivesIcon} for the configuration structure.
 */
@Component({
  selector: 'app-databox-lives-icon',
  templateUrl: './databox-lives-icon.component.html',
  styleUrl: './databox-lives-icon.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FaIconComponent],
})
export class DataboxLivesIconComponent {
  /**
   * The Config as provided to the element.
   *
   * Consumers may bind `null` / `undefined` sources; the built-in defaults
   * are used until a usable config arrives.
   */
  readonly config = input<IDataboxLivesIcon | null>();

  /**
   * The last valid count received via {@link update}, or `undefined` while
   * the configured starting count applies.
   */
  private readonly livesRemaining = signal<number | undefined>(undefined);

  /**
   * The icon to display.
   */
  protected readonly icon = computed(() => this.config()?.icon ?? DEFAULT_ICON());

  /**
   * The lives count to display.
   */
  protected readonly lives = computed(() => {
    const configured = this.config()?.lives;
    const startingLives = typeof configured === 'number' && configured >= 0 ? configured : 0;
    return (this.livesRemaining() ?? startingLives).toString();
  });

  constructor() {
    // a new config returns the databox to its configured starting count
    effect(() => {
      this.config();
      untracked(() => this.livesRemaining.set(undefined));
    });
  }

  /**
   * Updates the component with the user's remaining lives.
   *
   * A non-numeric or negative value is ignored and the current count is kept.
   *
   * @param livesRemaining - the amount of lives remaining
   */
  update(livesRemaining: number) {
    if (typeof livesRemaining !== 'number' || livesRemaining < 0) {
      return;
    }
    this.livesRemaining.set(livesRemaining);
  }
}
